import type { z } from 'zod';
import { type TransformerConfig } from '../common/types';
import { createDecodeFromDbFn, createEncodeToDbFn, createTransformer } from '../common/utils';
import { StudentStudentIdSchema } from '../students/schemas';
import { ClassSchema, DBClassSchema, DBClassStudentsEntrySchema } from './schemas';

type Class = z.infer<typeof ClassSchema>;
type DBClass = z.infer<typeof DBClassSchema>;

export const ClassCodecConfig: TransformerConfig<typeof ClassSchema, typeof DBClassSchema> = {
  name: 'Class',
  clientSchema: ClassSchema,
  dbSchema: DBClassSchema,
  transformers: [
    // students: [{ studentId, name, schoolId, schoolLabel, schoolShortLabel, slackId }] ↔
    //   students: { studentId: { name, schoolId, schoolLabel, schoolShortLabel, slackId } }
    createTransformer<typeof ClassSchema, typeof DBClassSchema>({
      encoder: {
        sourceFields: ['students'] as const,
        encode: ({ students }) => ({
          students: (students as Class['students']).reduce(
            (
              acc: Record<
                z.infer<typeof StudentStudentIdSchema>,
                z.infer<typeof DBClassStudentsEntrySchema>
              >,
              student: Class['students'][number]
            ) => {
              acc[student.studentId] = {
                name: student.name,
                ...(student.schoolId ? { schoolId: student.schoolId } : {}),
                ...(student.schoolLabel ? { schoolLabel: student.schoolLabel } : {}),
                ...(student.schoolShortLabel ? { schoolShortLabel: student.schoolShortLabel } : {}),
                slackId: student.slackId,
              };
              return acc;
            },
            {} as Record<
              z.infer<typeof StudentStudentIdSchema>,
              z.infer<typeof DBClassStudentsEntrySchema>
            >
          ),
        }),
      },
      decoder: {
        sourceFields: ['students'] as const,
        decode: ({ students }) => ({
          students: Object.entries((students ?? {}) as NonNullable<DBClass['students']>).map(
            ([studentId, student]: [string, NonNullable<DBClass['students']>[string]]) => {
              return {
                studentId,
                ...student,
              };
            }
          ),
        }),
      },
    }),

    // activeDayTimeSortKey: computed from active, day, time
    // Uses dependencies for partial updates where only some fields are provided
    createTransformer<typeof ClassSchema, typeof DBClassSchema>({
      encoder: {
        sourceFields: ['active', 'day', 'time'] as const,
        dependencies: ['active', 'day', 'time'] as const,
        encode: ({ active, day, time }) => ({
          activeDayTimeSortKey: `${active}#${day}#${time}`,
          active,
          day,
          time,
        }),
      },
      decoder: {
        sourceFields: ['activeDayTimeSortKey'] as const,
        decode: () => ({}),
      },
    }),

    // activeStreamSortKey: computed from active, course, stream
    // Uses dependencies for partial updates where only some fields are provided
    createTransformer<typeof ClassSchema, typeof DBClassSchema>({
      encoder: {
        sourceFields: ['active', 'course', 'stream'] as const,
        dependencies: ['active', 'course', 'stream'] as const,
        encode: ({ active, course, stream }) => ({
          activeStreamSortKey: `${active}#${course}#${stream}`,
          active,
          course,
          stream,
        }),
      },
      decoder: {
        sourceFields: ['activeStreamSortKey'] as const,
        decode: () => ({}),
      },
    }),

    // streams: the stream in use per course, kept in step with `course` and `stream` on every
    // write that touches either. A map in the write replaces the stored one; without one the
    // entries for the class's other courses come from the map it already holds — the encoded
    // class, or the existing class on a partial update, whose own course and stream stand in
    // for a map it was stored without — so a course change adds the new course's entry and
    // keeps the old one
    createTransformer<typeof ClassSchema, typeof DBClassSchema>({
      encoder: {
        sourceFields: ['course', 'stream', 'streams'] as const,
        dependencies: ['course', 'stream'] as const,
        encode: ({ course, stream, streams }, existing) => ({
          course,
          stream,
          streams: {
            ...((streams as Class['streams']) ?? {
              ...existing?.streams,
              ...(existing?.course !== undefined && existing.stream !== undefined
                ? { [existing.course]: existing.stream }
                : {}),
            }),
            [course as Class['course']]: stream as Class['stream'],
          },
        }),
      },
      // A class stored before the map existed reads as holding the stream in use for its course
      decoder: {
        sourceFields: ['course', 'stream', 'streams'] as const,
        decode: ({ course, stream, streams }) => ({
          ...(course !== undefined ? { course } : {}),
          ...(stream !== undefined ? { stream } : {}),
          ...(streams !== undefined
            ? { streams }
            : course !== undefined && stream !== undefined
              ? { streams: { [course as DBClass['course']]: stream as DBClass['stream'] } }
              : {}),
        }),
      },
    }),

    // slackChannelName: computed from course and classId
    // Uses dependencies for partial updates where only some fields are provided
    createTransformer<typeof ClassSchema, typeof DBClassSchema>({
      encoder: {
        sourceFields: ['course', 'classId'] as const,
        dependencies: ['course', 'classId'] as const,
        encode: ({ course, classId }) => ({
          slackChannelName: `cl-${course}-${classId}`,
          course,
          classId,
        }),
      },
      decoder: {
        sourceFields: ['slackChannelName'] as const,
        decode: ({ slackChannelName }) => ({
          slackChannelName,
        }),
      },
    }),
  ],
};

export const encodeClassToDb = createEncodeToDbFn(ClassCodecConfig);
export const decodeClassFromDb = createDecodeFromDbFn(ClassCodecConfig);
